import { AdditionalFieldDelegateProps } from "@firecms/core";
import { Chip } from "@firecms/ui";

import {
  AppleDelegatedOnboardingEntity,
  AppleEnvScoped,
  BRAND_ASSET_PAIR_STATE_LABELS,
  OnboardingGalleryPhotoEntity,
  OnboardingShowcaseRequestEntity,
  OnboardingStepName,
  OnboardingStepStatus,
} from "./apple_delegated_onboarding.types";
import {
  BRAND_ASSET_PAIR_STATE_COLORS,
  friendlyStepStatus,
  resolveEnvBucket,
  SUBMIT_STATE_COLORS,
} from "./apple_delegated_onboarding.utils";

/** Any step-shaped value the chip can read: the status fields plus the env-scoped Apple result. */
type ChipStatus = OnboardingStepStatus & { apple?: AppleEnvScoped<unknown> | null };

/**
 * One step's intent status as a coloured chip.
 *
 * ⚠️ THE ENVIRONMENT IS PART OF THE LABEL, NOT AN IMPLICATION. `submitState` is flat and
 * env-agnostic while Apple's results are bucketed under `apple.<PRODUCTION|AIE|DQE>`, so a step run
 * in DQE reads "Submitted to Apple" everywhere. Naming the environment the result actually landed in
 * is the cheapest way to stop that being read as "live in production" — which today it never is,
 * because the deployed backend writes the DQE bucket.
 */
export function OnboardingStatusChip({ status }: { status: ChipStatus | null | undefined }) {
  const label = friendlyStepStatus(status);
  const resolved = resolveEnvBucket(status?.apple);
  return (
    <Chip colorScheme={SUBMIT_STATE_COLORS[status?.submitState ?? ""] ?? "grayLight"} size="small">
      {resolved ? `${label} · ${resolved.env}` : label}
    </Chip>
  );
}

/**
 * The brand-asset PAIR state, as ONE chip.
 *
 * ⚠️ IT RENDERS A BACKEND-COMPUTED VALUE AND CONTAINS NO TRUTH TABLE. Mapping two Apple asset states
 * onto one word is an ordered decision cascade, and it lives in the backend where there is a test
 * runner to hold it. This looks up a label and a colour; that is the whole of its logic.
 *
 * ⚠️ An UNRECOGNISED value renders as itself in a neutral chip, never as blank. A pair state newer
 * than this build then shows staff the raw word — ugly but honest, and diagnosable — instead of an
 * empty cell that reads as "nothing here".
 */
export function BrandAssetPairStateChip({ pairState }: { pairState: string | null | undefined }) {
  if (!pairState) return null;
  return (
    <Chip colorScheme={BRAND_ASSET_PAIR_STATE_COLORS[pairState] ?? "grayLight"} size="small">
      {BRAND_ASSET_PAIR_STATE_LABELS[pairState] ?? pairState}
    </Chip>
  );
}

// One table cell per step rather than one parameterised builder: `AdditionalFieldDelegate.Builder` is
// handed a fixed props shape, so the step name cannot be passed in. Each is two lines and reads
// exactly as the column it renders.

type OnboardingCellProps = AdditionalFieldDelegateProps<Partial<AppleDelegatedOnboardingEntity>>;

const stepCell = (entity: OnboardingCellProps["entity"], step: OnboardingStepName) => (
  <OnboardingStatusChip status={entity.values[step]} />
);

export function BrandStepChip({ entity }: OnboardingCellProps) {
  return stepCell(entity, "brand");
}

export function BrandAssetsStepChip({ entity }: OnboardingCellProps) {
  return stepCell(entity, "brandAssets");
}

export function MediaStepChip({ entity }: OnboardingCellProps) {
  return stepCell(entity, "media");
}

export function LocationStepChip({ entity }: OnboardingCellProps) {
  return stepCell(entity, "location");
}

export function LocationCtaStepChip({ entity }: OnboardingCellProps) {
  return stepCell(entity, "locationCta");
}

export function LocationDetailsStepChip({ entity }: OnboardingCellProps) {
  return stepCell(entity, "locationDetails");
}

/**
 * The showcase subcollection's chip.
 *
 * ⚠️ Its status fields sit at the DOCUMENT ROOT (`submitState`, not `showcase.submitState`) — one
 * request per document, so there is nothing to nest under — which is why this reads `entity.values`
 * directly rather than a step key.
 */
export function ShowcaseRequestChip({
  entity,
}: AdditionalFieldDelegateProps<Partial<OnboardingShowcaseRequestEntity>>) {
  return <OnboardingStatusChip status={entity.values} />;
}

/**
 * The gallery subcollection's chip — THE SAME COMPONENT, not a parallel one.
 *
 * ⚠️ Its status fields sit at the DOCUMENT ROOT (`submitState`, not `galleryPhoto.submitState`) — one
 * photo per document, so there is nothing to nest under — which is why this reads `entity.values`
 * directly, exactly as `ShowcaseRequestChip` above does. The only difference between the two is the
 * entity type; both render `OnboardingStatusChip`, so a photo and a showcase cannot end up describing
 * the same `submitState` in different words.
 */
export function GalleryPhotoChip({
  entity,
}: AdditionalFieldDelegateProps<Partial<OnboardingGalleryPhotoEntity>>) {
  return <OnboardingStatusChip status={entity.values} />;
}
